/**
 * ResourceNode component -- renders a single resource as an SVG group.
 *
 * Features:
 * - Provider accent bar on the left edge (color-coded by cloud provider)
 * - Status-aware fill and stroke colors
 * - Truncated name and short type label
 * - Status badge with pulse animation for in-progress states
 * - Selected state with highlight glow
 * - Hover tooltip via <title>
 */

import { Component, createMemo, Show } from "solid-js";
import type { PulumiResource } from "./types";
import {
  getProviderFromType,
  getShortType,
  getStatusColors,
  PROVIDER_COLORS,
} from "./layout";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ResourceNodeProps {
  resource: PulumiResource;
  x: number;
  y: number;
  width: number;
  height: number;
  selected?: boolean;
  onClick?: (resource: PulumiResource) => void;
  showStatus?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ResourceNode: Component<ResourceNodeProps> = (props) => {
  const provider = createMemo(() => getProviderFromType(props.resource.type));
  const providerColor = createMemo(
    () => PROVIDER_COLORS[provider()] ?? PROVIDER_COLORS.unknown,
  );
  const statusColor = createMemo(() => getStatusColors(props.resource.status));
  const shortType = createMemo(() => getShortType(props.resource.type));

  const isInProgress = createMemo(() =>
    ["creating", "updating", "deleting"].includes(props.resource.status),
  );

  const displayName = createMemo(() => {
    const name = props.resource.name;
    return name.length > 24 ? `${name.slice(0, 22)}...` : name;
  });

  const displayType = createMemo(() => {
    const t = shortType();
    return t.length > 22 ? `${t.slice(0, 20)}...` : t;
  });

  const handleClick = () => {
    props.onClick?.(props.resource);
  };

  const showStatus = () => props.showStatus !== false;

  return (
    <g
      class="resource-graph-node"
      transform={`translate(${props.x}, ${props.y})`}
      onClick={handleClick}
      style={{ cursor: "pointer" }}
      data-testid={`resource-node-${props.resource.name}`}
    >
      {/* Tooltip */}
      <title>{`${props.resource.name}\n${props.resource.type}\nStatus: ${props.resource.status}`}</title>

      {/* Drop shadow */}
      <rect
        x="2"
        y="2"
        width={props.width}
        height={props.height}
        rx="8"
        fill="rgba(0,0,0,0.08)"
      />

      {/* Main background */}
      <rect
        x="0"
        y="0"
        width={props.width}
        height={props.height}
        rx="8"
        fill={statusColor().fill}
        stroke={props.selected ? "#3B82F6" : statusColor().stroke}
        stroke-width={props.selected ? "3" : "1.5"}
      />

      {/* Provider accent bar (left edge) */}
      <rect
        x="0"
        y="0"
        width="6"
        height={props.height}
        rx="0"
        fill={providerColor().bg}
        clip-path="inset(0 0 0 0 round 8px 0 0 8px)"
      />
      {/* Cover the rounded corner gap */}
      <rect x="0" y="4" width="6" height={props.height - 8} fill={providerColor().bg} />

      {/* Resource name */}
      <text
        x="14"
        y="24"
        fill={statusColor().text}
        font-size="13"
        font-weight="600"
        class="resource-graph-node-name"
      >
        {displayName()}
      </text>

      {/* Resource type */}
      <text
        x="14"
        y="42"
        fill={statusColor().text}
        font-size="10"
        opacity="0.8"
        class="resource-graph-node-type"
      >
        {displayType()}
      </text>

      {/* Status badge (only when showStatus is true) */}
      <Show when={showStatus()}>
        <g transform={`translate(${props.width - 66}, 6)`}>
          <rect
            x="0"
            y="0"
            width="56"
            height="18"
            rx="4"
            fill="rgba(255,255,255,0.25)"
          />
          <Show when={isInProgress()}>
            <circle
              cx="10"
              cy="9"
              r="3"
              fill={statusColor().text}
              class="resource-graph-pulse"
            />
          </Show>
          <text
            x={isInProgress() ? "17" : "6"}
            y="13"
            fill={statusColor().text}
            font-size="9"
            font-weight="500"
          >
            {props.resource.status}
          </text>
        </g>
      </Show>

      {/* Selection glow */}
      <Show when={props.selected}>
        <rect
          x="-3"
          y="-3"
          width={props.width + 6}
          height={props.height + 6}
          rx="10"
          fill="none"
          stroke="#3B82F6"
          stroke-width="1.5"
          opacity="0.5"
          class="resource-graph-pulse"
        />
      </Show>
    </g>
  );
};

export default ResourceNode;
